/**
 * Bilder im HTML-Editor — eingebettet als `data:`-URL.
 *
 * Bewusst **ohne** `@tiptap/extension-image`: das Paket zieht eine neuere
 * `@tiptap/core`-Version nach, wodurch npm 120 Cypress-Abhängigkeiten aus der
 * Sperrdatei entfernt und der Editor auf einen anderen Kern gehoben würde. Der
 * Knoten hier ist wenige Zeilen groß, nutzt nur den vorhandenen Kern und lässt
 * uns `data:`-Quellen ausdrücklich zu — genau das brauchen Signaturen, weil ein
 * Logo in der E-Mail mitreisen muss und nicht von einem Server nachgeladen
 * werden darf.
 *
 * Diese Datei enthält **nur** die Rechenteile und importiert bewusst kein
 * tiptap — `@tiptap/core` zieht `@tiptap/pm` nach, das als reines ESM-Paket von
 * der Testumgebung nicht übersetzt wird. Der Editor-Knoten liegt daher in
 * `EditorImageNode.ts`.
 */

/** Längste Kante nach dem Verkleinern. Für ein Signatur-Logo reichlich. */
export const MAX_EDGE_PX = 600;

/** Qualität beim Neukodieren als JPEG. */
export const JPEG_QUALITY = 0.85;

/**
 * Obergrenze je Bild nach dem Verkleinern. Ein Logo mit 600 px Kantenlänge
 * landet bei 20–60 KB; 512 KB lassen also viel Luft und verhindern trotzdem,
 * dass eine Signatur jede einzelne E-Mail um Megabytes aufbläht.
 */
export const MAX_IMAGE_BYTES = 512 * 1024;

/** Bildarten, die jeder Mailclient darstellt. */
export const ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Zielmaße unter Beibehaltung des Seitenverhältnisses. Kleinere Bilder werden
 * nicht vergrößert — ein hochskaliertes Logo wird nicht schärfer, nur größer.
 */
export const targetDimensions = (
  width: number,
  height: number,
  maxEdge: number = MAX_EDGE_PX
): Dimensions => {
  if (!width || !height || width <= 0 || height <= 0) {
    return { width: 0, height: 0 };
  }

  const longest = Math.max(width, height);

  if (longest <= maxEdge) {
    return { width: width, height: height };
  }

  const factor = maxEdge / longest;

  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
  };
};

/** Nutzdatengröße einer `data:`-URL in Byte (ohne Kopf, mit Auffüllung). */
export const dataUrlByteSize = (dataUrl: string): number => {
  if (!dataUrl) {
    return 0;
  }

  const commaAt = dataUrl.indexOf(',');
  const base64 = commaAt >= 0 ? dataUrl.slice(commaAt + 1) : dataUrl;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;

  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
};

export const formatBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${bytes} Bytes`;
};

/** Ist die Datei eine Bildart, die wir einbetten? */
export const isSupportedImage = (mimeType: string): boolean =>
  ALLOWED_MIME_TYPES.includes((mimeType || '').toLowerCase());
